/**
 * Opt-in positioning for a child app that arrived in the parent display.
 *
 * The parent markup names a measurement edge:
 *   <div data-anear-dock-anchor="board"></div>
 *
 * The child host from that same display asks to be measured against it and
 * names the custom property its own CSS reads:
 *   <anear-app
 *     name="any-app"
 *     data-anear-dock-anchor="board"
 *     data-anear-dock-var="--expanded-top">
 *   </anear-app>
 *
 * ABR writes the anchor's bottom, in pixels relative to the child-apps
 * overlay, onto that custom property. Hosts without both attributes are left
 * alone. The app slug is never consulted.
 */

export const DOCK_ANCHOR_ATTR = 'data-anear-dock-anchor'
export const DOCK_VAR_ATTR = 'data-anear-dock-var'

const CUSTOM_PROP = /^--[A-Za-z_][A-Za-z0-9_-]*$/

const escapeAttrValue = (value) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value)
  }
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

const declaredDock = (el) => {
  const anchorName = (el.getAttribute(DOCK_ANCHOR_ATTR) || '').trim()
  const cssVar = (el.getAttribute(DOCK_VAR_ATTR) || '').trim()
  if (!anchorName || !CUSTOM_PROP.test(cssVar)) return null
  return { anchorName, cssVar }
}

const clearDockVar = (el) => {
  if (!el._anearDockVar) return
  el.style.removeProperty(el._anearDockVar)
  el._anearDockVar = null
}

const findAnchor = (frameEl, anchorName) => {
  const selector = `[${DOCK_ANCHOR_ATTR}="${escapeAttrValue(anchorName)}"]`
  const matches = frameEl.querySelectorAll(selector)
  for (const el of matches) {
    if (el.tagName.toLowerCase() !== 'anear-app') return el
  }
  return null
}

export function childAppsRequestDocking(childAppsContainer) {
  if (!childAppsContainer) return false
  const hosts = childAppsContainer.querySelectorAll('anear-app')
  for (const el of hosts) {
    if (declaredDock(el) || el._anearDockVar) return true
  }
  return false
}

export function listDockAnchors(frameEl) {
  if (!frameEl) return []
  return Array.from(frameEl.querySelectorAll(`[${DOCK_ANCHOR_ATTR}]`)).filter(
    (el) => el.tagName.toLowerCase() !== 'anear-app'
  )
}

export function applyChildAppDocking(frameEl, childAppsContainer) {
  if (!frameEl || !childAppsContainer) return

  const hosts = Array.from(childAppsContainer.querySelectorAll('anear-app'))
  const containerTop = childAppsContainer.getBoundingClientRect().top

  hosts.forEach((el) => {
    const dock = declaredDock(el)
    if (!dock) {
      clearDockVar(el)
      return
    }

    if (el._anearDockVar && el._anearDockVar !== dock.cssVar) {
      el.style.removeProperty(el._anearDockVar)
      el._anearDockVar = null
    }

    // Hidden hosts stay mounted. Leave the last measurement in place.
    if (el.getAttribute('data-parent-hidden') === '1') return

    const anchor = findAnchor(frameEl, dock.anchorName)
    if (!anchor) {
      el.style.removeProperty(dock.cssVar)
      el._anearDockVar = null
      return
    }

    const topPx = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom - containerTop))
    el.style.setProperty(dock.cssVar, `${topPx}px`)
    el._anearDockVar = dock.cssVar
  })
}
